import React, { useState } from 'react';
import { useAppSelector } from 'hooks';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Collapse, Row } from 'reactstrap';
import AppointmentActivitiesMapModal from '~appointments/AppointmentActivitiesMapModal';

import { formatDateString, formatDayString, formatDayTimeString, formatTimeStringAMPM } from '~libs/dayjs';

import { Button } from '~components';
import Icon, { faChevronDown, faChevronUp } from '~components/icon';

import { selectAppointmentActivities } from '~appointments/selectors/activities';

interface Props {
  className: string;
}

function AppointmentActivities({ className }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>();
  const [mapTitle, setMapTitle] = useState<string>('');
  const rows = useAppSelector(selectAppointmentActivities);

  // A "#<id>" reference to another appointment inside a plain-text detail line. Written by the API
  // when DEX data is copied from one appointment to another - the activity table is a
  // legacy TP1 table with no column to carry a related id, so the reference travels in the message
  // itself and is turned back into a link here. The trailing negative lookahead stops a value like
  // "#551ABC" (or an id immediately followed by an underscore) from being read as appointment 551
  // with "ABC" left dangling as plain text - only a numeric run that ends at a real token boundary
  // is an appointment reference.
  const appointmentReferencePattern = /#(\d+)(?![A-Za-z0-9_])/g;

  // Only a DEX copy entry carries an appointment reference, and it now sits in the activity's type
  // ("DEX Details Copied from #<id>") rather than its details - every other activity type's
  // free-text can legitimately contain an unrelated "#<number>" (a case, invoice or ticket number)
  // that must not become a misleading link to an unrelated or non-existent appointment.
  const DEX_COPY_ACTIVITY_TYPE = 'DEX Details Copied';

  const linkifyAppointmentReferences = (text: string) => {
    const segments = text.split(appointmentReferencePattern);
    // String.split with a capturing group alternates literal text and captured ids, so every odd
    // index is an appointment id and every even index is the surrounding text.
    return segments.map((segment, index) =>
      index % 2 === 1 ? (
        <Link key={index} to={`/appointments/${segment}`} data-cy="activity-appointment-link">
          #{segment}
        </Link>
      ) : (
        <React.Fragment key={index}>{segment}</React.Fragment>
      ),
    );
  };

  // Build sub-section of log details based on array of object values. Details are always plain
  // text: the only appointment reference an activity carries is the DEX copy's source, and that
  // lives in the type, so nothing here needs linkifying.
  const buildDetails = (details: Array<{ field: string; from: string; to: string } | string>) =>
    details.map((data, index) => (
      <Row key={index} className="ms-4 mb-1">
        {typeof data === 'object' ? `${data.field}: "${data.from}" to "${data.to}"` : data}
      </Row>
    ));

  // Build rows of activities based on appointment activities within reducer.
  const buildRows = (day: string) => {
    const dayRows = rows.filter(({ date_time }) => day === formatDayString(date_time));
    return dayRows.map(({ date_time, details, type, user, coords }, index) => (
      <Row key={index} className="mb-1">
        <Col sm={1}>{formatTimeStringAMPM(date_time)}</Col>

        <Col sm={3}>
          {user?.full_name ?? ''}{' '}
          {coords && (
            <Button
              size="sm"
              className="mx-1"
              onClick={() => {
                setShowMapModal(true);
                setMapCoords({ lat: coords.latitude, lng: coords.longitude });
                setMapTitle(`Activity location at ${formatDayTimeString(date_time)}`);
              }}
            >
              View On Map
            </Button>
          )}
        </Col>
        <Col>
          <Row className="mb-1">
            {typeof type === 'string' && type.startsWith(DEX_COPY_ACTIVITY_TYPE)
              ? linkifyAppointmentReferences(type)
              : type}
          </Row>
          {details ? buildDetails(details) : ''}
        </Col>
      </Row>
    ));
  };

  // Create group of unique "days" to group activities together
  const dates = [...new Set(rows.map(({ date_time }) => formatDateString(date_time)))];
  const bodyRows = dates.map((date, index) => (
    <div key={index}>
      <Row className="fw-bold mb-1">{date}</Row>
      {buildRows(date)}
    </div>
  ));

  return (
    <div className={className}>
      <Card className="rounded-0">
        <CardHeader
          className="d-flex justify-content-between border-0 fw-bold"
          type={'button'}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div>Activity Logs</div>
          <Icon icon={isOpen ? faChevronUp : faChevronDown} className="ms-2" />
        </CardHeader>
        <Collapse isOpen={isOpen}>
          <CardBody>{bodyRows}</CardBody>
        </Collapse>
      </Card>
      <AppointmentActivitiesMapModal
        mapCoords={mapCoords}
        mapTitle={mapTitle}
        setMapCoords={setMapCoords}
        setMapTitle={setMapTitle}
        setShowMapModal={setShowMapModal}
        showMapModal={showMapModal}
      />
    </div>
  );
}

export default AppointmentActivities;
